import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { 
  Subscription, 
  SubscriptionPlan, 
  PlanValidationResult,
  SubscriptionLimits,
  FeatureAccess,
  SUBSCRIPTION_PLAN_IDS
} from '../types/subscription';
import { SUBSCRIPTION_PLANS, getPlanById, getFreePlan } from '../constants/subscriptionPlans';
import { useAuth } from './AuthContext';
import { useCache } from './CacheContext';
import { LocalUsageStats, LocalUsageService } from '../services/localUsageService';

// Context State Interface
interface SubscriptionState {
  // Current subscription
  currentSubscription: Subscription | null;
  currentPlan: SubscriptionPlan | null;
  
  // Available plans
  availablePlans: SubscriptionPlan[];
  
  // Loading states
  isLoading: boolean;
  isUpdating: boolean;
  
  // Error states
  error: string | null;
  
  // Validation
  planValidation: PlanValidationResult | null;
}

// Action Types
type SubscriptionAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_UPDATING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_SUBSCRIPTION'; payload: Subscription | null }
  | { type: 'SET_PLAN'; payload: SubscriptionPlan | null }
  | { type: 'SET_PLAN_VALIDATION'; payload: PlanValidationResult | null }
  | { type: 'RESET_STATE' };

// Initial State
const initialState: SubscriptionState = {
  currentSubscription: null,
  currentPlan: null,
  availablePlans: SUBSCRIPTION_PLANS,
  isLoading: false,
  isUpdating: false,
  error: null,
  planValidation: null,
};

// Reducer
const subscriptionReducer = (state: SubscriptionState, action: SubscriptionAction): SubscriptionState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_UPDATING':
      return { ...state, isUpdating: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'SET_SUBSCRIPTION':
      return { 
        ...state, 
        currentSubscription: action.payload,
        currentPlan: action.payload ? getPlanById(action.payload.planId) || null : null
      };
    
    case 'SET_PLAN':
      return { ...state, currentPlan: action.payload };
    
    case 'SET_PLAN_VALIDATION':
      return { ...state, planValidation: action.payload };
    
    case 'RESET_STATE':
      return initialState;
    
    default:
      return state;
  }
};

// Context Interface
interface SubscriptionContextType {
  // State
  state: SubscriptionState;
  
  // Actions
  loadSubscription: (userId: string) => Promise<void>;
  updateSubscription: (subscription: Partial<Subscription>) => Promise<void>;
  upgradePlan: (newPlanId: string) => Promise<void>;
  downgradePlan: (newPlanId: string) => Promise<void>;
  cancelSubscription: () => Promise<void>;
  validatePlan: (planId: string) => Promise<PlanValidationResult>;
  
  // Helpers
  getCurrentPlan: () => SubscriptionPlan | null;
  getFeatureAccess: () => FeatureAccess;
  getLimits: () => SubscriptionLimits | null;
  getUsageStats: () => LocalUsageStats | null;
  canCreateOffice: (currentCount: number) => boolean;
  canCreateClient: (currentCount: number) => boolean;
  canCreateFile: (currentCount: number) => boolean;
  isFeatureAvailable: (feature: keyof FeatureAccess) => boolean;
  
  // Utility
  clearError: () => void;
  resetState: () => void;
}

// Create Context
const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

// Provider Props
interface SubscriptionProviderProps {
  children: ReactNode;
}

// Provider Component
export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(subscriptionReducer, initialState);
  const { user } = useAuth();
  const { getUsageStats: getCacheUsageStats } = useCache();

  // Auto-load subscription data when user is available
  useEffect(() => {
    if (user?.id) {
      console.log('🚀 SubscriptionContext: User detected, loading subscription data for:', user.id);
      console.log('🚀 SubscriptionContext: User details:', {
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
        isActive: user.isActive
      });
      loadSubscriptionWithRetry(user.id);
      // Usage stats are now loaded from cache context
    } else {
      console.log('⏳ SubscriptionContext: No user ID available yet');
      console.log('⏳ SubscriptionContext: Current user object:', user);
    }
  }, [user?.id]);


  // Load subscription data with retry mechanism (for eventual consistency after signup)
  const loadSubscriptionWithRetry = async (userId: string, retryCount = 0): Promise<void> => {
    const maxRetries = 5;
    const retryDelay = 1000; // Start with 1 second
    
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      // Load real subscription data from Firestore
      const { subscriptionService } = await import('../services/subscriptionService');
      const subscription = await subscriptionService.getUserSubscription(userId);
      
      if (subscription) {
        console.log('✅ SubscriptionContext: Subscription found on attempt', retryCount + 1);
        
        // Calculate trial days remaining for debugging
        if (subscription.status === 'trial') {
          const now = Date.now();
          let trialDaysRemaining = 0;
          
          if (subscription.endDate) {
            const trialEnd = parseInt(subscription.endDate);
            trialDaysRemaining = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
          } else {
            // Use centralized trial calculation logic
            const { TrialExtensionUtils } = await import('../constants/trialExtension');
            const startDate = parseInt(subscription.startDate);
            const registrationDate = new Date(startDate);
            const calculatedTrialEndDate = TrialExtensionUtils.getTrialEndDate(registrationDate);
            trialDaysRemaining = Math.ceil((calculatedTrialEndDate.getTime() - now) / (1000 * 60 * 60 * 24));
          }
          
        }
        
        dispatch({ type: 'SET_SUBSCRIPTION', payload: subscription });
        dispatch({ type: 'SET_LOADING', payload: false });
      } else {
        // Subscription not found - retry if we haven't exceeded max retries
        if (retryCount < maxRetries) {
          const delay = retryDelay * Math.pow(2, retryCount); // Exponential backoff
          console.log(`⏳ SubscriptionContext: Subscription not found, retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
          setTimeout(() => {
            loadSubscriptionWithRetry(userId, retryCount + 1);
          }, delay);
        } else {
          // Max retries reached, use fallback
          console.log('⚠️ SubscriptionContext: Max retries reached, using fallback subscription');
          const freePlan = getFreePlan();
          const now = Date.now();
          // Use centralized paid subscription end date calculation
          const { TrialExtensionUtils } = await import('../constants/trialExtension');
          const oneMonthFromNow = TrialExtensionUtils.getPaidSubscriptionEndDate(new Date(now), 'monthly').getTime();
          
          const fallbackSubscription: Subscription = {
            id: 'fallback-subscription',
            userId,
            userName: '',
            userPhone: '',
            planId: freePlan.id,
            status: 'active',
            startDate: now.toString(),
            endDate: oneMonthFromNow.toString(),
            totalPrice: 0,
            currency: 'EGP',
            createdAt: now.toString(),
            updatedAt: now.toString(),
            createdBy: userId,
            updatedBy: userId,
          };
          
          console.log('📊 SubscriptionContext: Created fallback subscription:', fallbackSubscription);
          dispatch({ type: 'SET_SUBSCRIPTION', payload: fallbackSubscription });
          console.log('✅ SubscriptionContext: Fallback subscription dispatched to state');
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      }
    } catch (error) {
      console.error('❌ Error loading subscription:', error);
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to load subscription' });
      
      // Retry on error if we haven't exceeded max retries
      if (retryCount < maxRetries) {
        const delay = retryDelay * Math.pow(2, retryCount);
        console.log(`⏳ SubscriptionContext: Error occurred, retrying in ${delay}ms (attempt ${retryCount + 1}/${maxRetries})`);
        setTimeout(() => {
          loadSubscriptionWithRetry(userId, retryCount + 1);
        }, delay);
      } else {
        // Max retries reached, use error fallback
        const freePlan = getFreePlan();
        const now = Date.now();
        // Use centralized paid subscription end date calculation
        const { TrialExtensionUtils } = await import('../constants/trialExtension');
        const oneMonthFromNow = TrialExtensionUtils.getPaidSubscriptionEndDate(new Date(now), 'monthly').getTime();
        
        const errorFallbackSubscription: Subscription = {
          id: 'error-fallback-subscription',
          userId,
          userName: '',
          userPhone: '',
          planId: freePlan.id,
          status: 'active',
          startDate: now.toString(),
          endDate: oneMonthFromNow.toString(),
          totalPrice: 0,
          currency: 'EGP',
          createdAt: now.toString(),
          updatedAt: now.toString(),
          createdBy: userId,
          updatedBy: userId,
        };
        
        dispatch({ type: 'SET_SUBSCRIPTION', payload: errorFallbackSubscription });
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  };

  // Load subscription data (kept for backward compatibility)
  const loadSubscription = async (userId: string): Promise<void> => {
    await loadSubscriptionWithRetry(userId, 0);
  };

  // Note: Usage stats are now loaded from cache context via getUsageStats()

  // Update subscription
  const updateSubscription = async (subscription: Partial<Subscription>): Promise<void> => {
    try {
      dispatch({ type: 'SET_UPDATING', payload: true });
      
      // TODO: Implement actual API call
      // await subscriptionService.updateSubscription(subscription);
      
      if (state.currentSubscription) {
        const updatedSubscription = { ...state.currentSubscription, ...subscription };
        dispatch({ type: 'SET_SUBSCRIPTION', payload: updatedSubscription });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to update subscription' });
    } finally {
      dispatch({ type: 'SET_UPDATING', payload: false });
    }
  };

  // Upgrade plan
  const upgradePlan = async (newPlanId: string): Promise<void> => {
    try {
      dispatch({ type: 'SET_UPDATING', payload: true });
      
      // TODO: Implement actual API call
      // await subscriptionService.upgradePlan(state.currentSubscription?.id, newPlanId);
      
      if (state.currentSubscription) {
        const updatedSubscription = { ...state.currentSubscription, planId: newPlanId };
        dispatch({ type: 'SET_SUBSCRIPTION', payload: updatedSubscription });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to upgrade plan' });
    } finally {
      dispatch({ type: 'SET_UPDATING', payload: false });
    }
  };

  // Downgrade plan
  const downgradePlan = async (newPlanId: string): Promise<void> => {
    try {
      dispatch({ type: 'SET_UPDATING', payload: true });
      
      // TODO: Implement actual API call
      // await subscriptionService.downgradePlan(state.currentSubscription?.id, newPlanId);
      
      if (state.currentSubscription) {
        const updatedSubscription = { ...state.currentSubscription, planId: newPlanId };
        dispatch({ type: 'SET_SUBSCRIPTION', payload: updatedSubscription });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to downgrade plan' });
    } finally {
      dispatch({ type: 'SET_UPDATING', payload: false });
    }
  };

  // Cancel subscription
  const cancelSubscription = async (): Promise<void> => {
    try {
      dispatch({ type: 'SET_UPDATING', payload: true });
      
      // TODO: Implement actual API call
      // await subscriptionService.cancelSubscription(state.currentSubscription?.id);
      
      if (state.currentSubscription) {
        const updatedSubscription = { 
          ...state.currentSubscription, 
          status: 'cancelled' as const
        };
        dispatch({ type: 'SET_SUBSCRIPTION', payload: updatedSubscription });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to cancel subscription' });
    } finally {
      dispatch({ type: 'SET_UPDATING', payload: false });
    }
  };

  // Validate plan
  const validatePlan = async (planId: string): Promise<PlanValidationResult> => {
    try {
      const plan = getPlanById(planId);
      if (!plan) {
        throw new Error('Plan not found');
      }

      // Get usage stats from cache
      const usageStats = getCacheUsageStats();
      if (!usageStats) {
        throw new Error('Usage stats not available');
      }

      const limits: SubscriptionLimits = {
        maxOffices: plan.maxOffices,
        maxClientsTotal: plan.maxClientsTotal,
        maxFilesTotal: plan.maxFilesTotal,
        maxTeamMembers: plan.maxTeamMembers
      };

      const exceededLimits: string[] = [];
      
      if (limits.maxOffices !== -1 && usageStats.officesCount >= limits.maxOffices) {
        exceededLimits.push('offices');
      }
      if (limits.maxClientsTotal !== -1 && usageStats.clientsCount >= limits.maxClientsTotal) {
        exceededLimits.push('clients');
      }
      if (limits.maxFilesTotal !== -1 && usageStats.filesCount >= limits.maxFilesTotal) {
        exceededLimits.push('files');
      }

      const validation: PlanValidationResult = {
        isValid: exceededLimits.length === 0,
        canUpgrade: true, // TODO: Implement upgrade logic
        canDowngrade: true, // TODO: Implement downgrade logic
        limits,
        currentUsage: usageStats,
        exceededLimits,
        suggestedPlan: exceededLimits.length > 0 ? SUBSCRIPTION_PLAN_IDS.PROFESSIONAL : undefined
      };

      dispatch({ type: 'SET_PLAN_VALIDATION', payload: validation });
      return validation;
    } catch (error) {
      throw error;
    }
  };

  // Helper functions
  const getCurrentPlan = (): SubscriptionPlan | null => {
    return state.currentPlan;
  };

  const getFeatureAccess = (): FeatureAccess => {
    const plan = state.currentPlan;
    const subscription = state.currentSubscription;
    
    console.log('🔍 getFeatureAccess called:');
    console.log('  - Current plan:', plan);
    console.log('  - Current subscription:', subscription);
    
    if (!plan) {
      console.log('  - No plan found, returning default access (all false)');
      return {
        eventReminders: false,
        advancedStats: false,
        community: false,
        phoneSupport: false,
        teamManagement: false,
        exportExcel: false
      };
    }

    const featureAccess = {
      eventReminders: plan.includesEventReminders,
      advancedStats: plan.includesAdvancedStats,
      community: plan.includesCommunity,
      phoneSupport: plan.includesPhoneSupport,
      teamManagement: plan.teamMemberPrice !== undefined,
      exportExcel: plan.includesExportExcel
    };
    
    console.log('  - Feature access calculated:', featureAccess);
    return featureAccess;
  };

  const getUsageStats = (): LocalUsageStats | null => {
    return getCacheUsageStats();
  };

  const getLimits = (): SubscriptionLimits | null => {
    const plan = state.currentPlan;
    if (!plan) return null;

    return {
      maxOffices: plan.maxOffices,
      maxClientsTotal: plan.maxClientsTotal,
      maxFilesTotal: plan.maxFilesTotal,
      maxTeamMembers: plan.maxTeamMembers
    };
  };

  const canCreateOffice = (currentCount: number): boolean => {
    const limits = getLimits();
    if (!limits) return false;
    return limits.maxOffices === -1 || currentCount < limits.maxOffices;
  };

  const canCreateClient = (currentCount: number): boolean => {
    const limits = getLimits();
    if (!limits) return false;
    return limits.maxClientsTotal === -1 || currentCount < limits.maxClientsTotal;
  };

  const canCreateFile = (currentCount: number): boolean => {
    const limits = getLimits();
    if (!limits) return false;
    return limits.maxFilesTotal === -1 || currentCount < limits.maxFilesTotal;
  };

  const isFeatureAvailable = (feature: keyof FeatureAccess): boolean => {
    const featureAccess = getFeatureAccess();
    const isAvailable = featureAccess[feature];
    console.log(`🎯 isFeatureAvailable('${feature}'):`, isAvailable);
    return isAvailable;
  };

  const clearError = (): void => {
    dispatch({ type: 'SET_ERROR', payload: null });
  };

  const resetState = (): void => {
    dispatch({ type: 'RESET_STATE' });
  };

  const contextValue: SubscriptionContextType = {
    state,
    loadSubscription,
    updateSubscription,
    upgradePlan,
    downgradePlan,
    cancelSubscription,
    validatePlan,
    getCurrentPlan,
    getFeatureAccess,
    getLimits,
    getUsageStats,
    canCreateOffice,
    canCreateClient,
    canCreateFile,
    isFeatureAvailable,
    clearError,
    resetState,
  };

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// Hook to use subscription context
export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
